import * as vector from './vector'
import _ from 'lodash'

const Point = vector.Point

/**
 * Box component for calculating dimensions of a box
 */
class Box {
  /**
   * Creates a new Box instance.
   * @constructor
   * @param {Number} x1
   * @param {Number} y1
   * @param {Number} x2
   * @param {Number} y2
   */
  constructor (x1, y1, x2, y2) {
    this.x1 = x1
    this.y1 = y1
    this.x2 = x2
    this.y2 = y2

    this.minWidth = 0
    this.minHeight = 0
  }

  /**
   * Sets the min width of the box.
   * @returns {Object} the box
   */
  setMinWidth (width) {
    this.minWidth = width
  }

  /**
   * Sets the min height of the box.
   * @returns {Object} the box
   */
  setMinHeight (height) {
    this.minHeight = height
  }

  /**
   * Calculates the width of the box.
   * @returns {Number}
   */
  width () {
    return (this.x2 - this.x1) || 0
  }

  /**
   * Calculates the height of the box.
   * @returns {Number}
   */
  height () {
    return (this.y2 - this.y1) || 0
  }

  /**
   * Calculates the center of the box.
   * @returns {Point}
   */
  center () {
    return new Point(this.x1 + this.width() / 2, this.y1 + this.height() / 2)
  }

  getAbsolutePoint (position) {
    return {
      x: this.x1 + this.width() * position[0],
      y: this.y1 + this.height() * position[1]
    }
  }

  /**
   * Resizes the box to a new size.
   * @param {Number} newWidth
   * @param {Number} newHeight
   * @param {Array} [origin] The origin point to resize from.
   *      Defaults to [0, 0] (top left).
   * @param {Number} angle The angle of the box rotation.
   *      Defaults to 0.
   * @param {String} [grow] The axis to grow to maintain the ratio.
   * @param {Number} [ratio] The aspect ratio to maintain
   */
  resize (newWidth, newHeight, origin = [0, 0], angle = 0, grow, ratio) {
    const center = this.center()
        , previousWidth = this.width()
        , previousHeight = this.height()

    if (angle) {
      /*
       * Resize code is incredibly difficult for rotated wobs. The only way I could
       * get it working is by restricting a resize in one dimension. So you can resize
       * just the width or just the height of a rotated wob. If two dimensions need to change
       * call this function twice.
       */
      if (_.isEqual(origin, [.5, 0] || _.isEqual(origin, [.5, 1]))) {
        // Resize based on height, so force newWidth equal to the current width
        newWidth = null
      }
      if (_.isEqual(origin, [0, .5] || _.isEqual(origin, [1, .5]))) {
        // Resizing the width, force height to its current value
        newHeight = null
      }

      /* This box is rotated at some angle, which transforms around the middle of the box.
       * We need to make sure the originPoint is totally locked during a resize.
       * To do that, we calculate a vector `scaleDirection`, which points from the rotated locked handle (origin)
       * to the opposite handle. After we resize the box, we determine how much that vector changed to determine
       * the new center and run a translate to determine the new location for x1/y1
       */

      let originPoint = this.getAbsolutePoint(origin)

      // Scoped at var so we can access later.
      var rotatedOriginPoint = vector.rotate(angle, center)(originPoint)

      let movingHandle = vector.rotate(180, center)(new Point(rotatedOriginPoint.x, rotatedOriginPoint.y))

      // Scoped with var so we can access later
      var scaleDirection = rotatedOriginPoint.direction(movingHandle) // vector pointing from the center of the scale to the moving handle
    }

    // Change the coords in non-rotated space
    this._resize(newWidth, newHeight, origin)

    // Make sure we respect aspect ratio
    this.constrainToRatio(ratio, origin, grow, angle)

    // Make sure it's not too small
    this.constrainToSize(origin, ratio, angle)

    if (angle) {
      // Try to figure out how much the vector changed after the move
      let updatedCenter = this.center()
        , originPoint = this.getAbsolutePoint(origin)
        , updatedRotatedOriginPoint = vector.rotate(angle, updatedCenter)(originPoint)
        , movedHandle = vector.rotate(180, updatedCenter)(new Point(updatedRotatedOriginPoint.x, updatedRotatedOriginPoint.y))
        , updatedScaleDirection = updatedRotatedOriginPoint.direction(movedHandle)
        , updatedScale = new Point(updatedScaleDirection.x - scaleDirection.x, updatedScaleDirection.y - scaleDirection.y)
        , scaleDelta = updatedScale.component(scaleDirection)
        , scaleFactor = (scaleDirection.length + scaleDelta) / scaleDirection.length
        , scaledCenter = vector.scaleDir(scaleFactor, scaleDirection, rotatedOriginPoint)(center)
        , newWidth = this.width()
        , newHeight = this.height()

      const updatedTopLeft = vector.translate(new Point(-newWidth / 2, -newHeight / 2))(scaledCenter)

      // Seems like the constraints (aspect ratio and min) mess up this algorithm and I give up.
      // Not sure why x/y can be NaN
      if (_.isFinite(updatedTopLeft.x) && _.isFinite(updatedTopLeft.y)) {
        this.x1 = updatedTopLeft.x
        this.y1 = updatedTopLeft.y

        this.x2 = this.x1 + newWidth
        this.y2 = this.y1 + newHeight
      }
    }

  }

  /**
   * Resizes the box to a new size. Ignores rotation of the box
   * @param {Number} newWidth
   * @param {Number} newHeight
   * @param {Array} [origin] The origin point to resize from.
   *      Defaults to [0, 0] (top left).
   */
  _resize (newWidth, newHeight, origin = [0, 0]) {
    if (_.isNil(newWidth)) {
      newWidth = this.width()
    }
    if (_.isNil(newHeight)) {
      newHeight = this.height()
    }

    const fromX = this.x1 + (this.width() * origin[0])
    const fromY = this.y1 + (this.height() * origin[1])

    this.x1 = fromX - (newWidth * origin[0])
    this.y1 = fromY - (newHeight * origin[1])
    this.x2 = this.x1 + newWidth
    this.y2 = this.y1 + newHeight

    return this
  }

  /**
   * Constrain the box to a fixed ratio.
   * @param {Number} ratio
   * @param {Array} [origin] The origin point to resize from.
   *     Defaults to [0, 0] (top left).
   * @param {String} [grow] The axis to grow to maintain the ratio.
   *     Defaults to 'height'.
   */
  constrainToRatio (ratio, origin = [0, 0], grow = 'height', angle = 0) {
    if (!ratio) {
      return
    }

    switch (grow) {
      case 'width': // Grow width only
        let height = angle ? null : this.height() // If rotated, we can't change the height
        origin = angle ? [0, .5] : origin
        this._resize(this.height() * 1 / ratio, height, origin, angle)
        break
      case 'height': // Grow height only
      default: // Default: Grow height only
        let width = angle ? null : this.width() // If it's rotated, be sure to not pass in a width
        origin = angle ? [.5, 0] : origin
        this._resize(width, this.width() * ratio, origin, angle)
    }

    return this
  }

  /**
   * Constrain the box to the minimum size of its dimensions
   * @param {Array} [origin] The origin point to resize from.
   *     Defaults to [0, 0] (top left).
   * @param {Number} [ratio] Ratio to maintain.
   */
  constrainToSize (origin = [0, 0], ratio = null, angle = 0) {
    let minHeight = this.minHeight
      , minWidth = this.minWidth

    // Calculate new min width/height that constrains to the ratio
    if (ratio) {
      if (ratio > 1) {
        minHeight = minHeight * ratio
      } else if (ratio < 1) {
        minWidth = minHeight * 1 / ratio
      }
    }

    if (minWidth && this.width() < minWidth) {
      let newHeight = ratio === null ? this.height() : minHeight
        , newWidth = minWidth

      if (angle) {
        // Have to do one at a time
        this._resize(null, newHeight, [.5, 0], angle) // Height
        this._resize(newWidth, null, [0, .5], angle) // Then width
      } else {
        this._resize(newWidth, newHeight, origin, angle)
      }
    }

    if (minHeight && this.height() < minHeight) {
      let newWidth = ratio === null ? this.width() : minWidth
        , newHeight = minHeight

      if (angle) {
        this._resize(null, newHeight, [.5, 0], angle) // Height
        this._resize(newWidth, null, [0, .5], angle) // Then width
      } else {
        this._resize(newWidth, newHeight, origin, angle)
      }
    }

    return this
  }
}

export default Box
